import { AmenityDurationDetails } from "@/routes/amenity-space";
import {
  AmenityBookingRequest,
  BaseAmenitySpace,
  BookingAvailabilityType
} from "@/types/Bookings";
import formatCurrency from "@/utils/formatCurrency";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";

type Props = { amenitySpace: BaseAmenitySpace; booking: AmenityBookingRequest };

export default function Header({ amenitySpace, booking }: Props) {
  const { name, price, bookingAvailabilityType } = amenitySpace;
  const { startTime, endTime } = booking;
  const { t } = useTranslation();

  return (
    <>
      <div className="items-center justify-between p-4 mb-5 bg-gray-100 rounded-md md:flex">
        <div className="flex-1 pr-6 overflow-hidden">
          <div className="flex items-center">
            <p className="mr-4 whitespace-nowrap">{name}</p>
            {price > 0 ? (
              <span className="inline-flex items-center rounded-md bg-blue-100 px-2.5 py-0.5 text-sm font-medium text-blue-800">
                {bookingAvailabilityType === BookingAvailabilityType.ByDay
                  ? t(`amenity.space.price-by-day`, {
                      amount: formatCurrency(price).replace(".00", "")
                    })
                  : t(`amenity.space.price-nightly`, {
                      amount: formatCurrency(price).replace(".00", "")
                    })}
              </span>
            ) : null}
          </div>
          <AmenityDurationDetails
            minDuration={amenitySpace.minDuration}
            maxDuration={amenitySpace.maxDuration}
            bookingAvailabilityType={amenitySpace.bookingAvailabilityType}
          />
        </div>
        <div className="flex items-center flex-1 pt-2 border-gray-300 md:border-l md:pl-6 md:pt-0">
          {amenitySpace.bookingAvailabilityType ===
          BookingAvailabilityType.Overnight ? (
            <div className="flex-1">
              <div className="flex">
                <div className="w-[120px] mr-2">
                  <FontAwesomeIcon icon={["fal", "calendar"]} /> Check in
                </div>
                <p>
                  {startTime
                    ? moment
                        .tz(startTime, amenitySpace.timezone)
                        .format("dddd, MMMM D")
                    : "-"}
                </p>
              </div>
              <div className="flex">
                <div className="w-[120px] mr-2">
                  <FontAwesomeIcon icon={["fal", "calendar"]} /> Check out
                </div>
                <p>
                  {endTime
                    ? moment
                        .tz(endTime, amenitySpace.timezone)
                        .format("dddd, MMMM D")
                    : "-"}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex-1">
              <div className="flex">
                <div className="w-[120px] mr-2">
                  <FontAwesomeIcon icon={["fal", "calendar"]} />
                </div>
                <p>
                  {startTime ? moment(startTime).format("dddd, MMMM D") : "-"}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      {amenitySpace.isConditionalAccessEnabled && (
        <div className="flex gap-3 p-4 mb-5 bg-gray-50 border-l-4 border-blue-500 rounded-r-lg">
          <div className="flex-shrink-0 mt-0.5 text-blue-500">
            <FontAwesomeIcon icon="key" size="lg" />
          </div>
          <div>
            <p className="m-0 font-semibold">Key access required</p>
            <p className="m-0 mt-1 text-sm text-gray-500">
              Use the Livly app to unlock this amenity during your booking
              window.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
